import React, { useState, useEffect } from 'react';
import {
  FaShare as Share,
  FaCopy as Copy,
  FaUsers as Users,
  FaTrophy as Trophy,
  FaGift as Gift,
  FaChartLine as Chart,
  FaWhatsapp as WhatsApp,
  FaTelegram as Telegram,
  FaEnvelope as Email,
  FaLink as Link,
  FaCoins as Coins,
  FaStar as Star,
  FaCrown as Crown,
  FaMedal as Medal,
  FaRocket as Rocket,
  FaGem as Diamond,
  FaCalendarCheck as CalendarCheck,
  FaCheckCircle as CheckCircle,
  FaClock as Clock,
  FaExclamationTriangle as Warning,
} from 'react-icons/fa';
import {
  UserReferralProfile,
  ReferralProgram,
  UserReferral,
  ReferralLevel,
  IssuedReward,
  ReferralAchievement,
} from '../../types/coupons';
import { useAuth } from '../../hooks/useAuth';

interface ReferralSystemProps {}

const ReferralSystem: React.FC<ReferralSystemProps> = () => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<UserReferralProfile | null>(
    null
  );
  const [activePrograms, setActivePrograms] = useState<ReferralProgram[]>([]);
  const [userReferrals, setUserReferrals] = useState<UserReferral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedProgram, setSelectedProgram] =
    useState<ReferralProgram | null>(null);
  const [copiedText, setCopiedText] = useState('');

  // تحميل بيانات المستخدم والبرامج
  useEffect(() => {
    // بيانات تجريبية لملف المستخدم
    const mockProfile: UserReferralProfile = {
      userId: user?.id || '1',
      referralCode: 'STUDENT2024',
      totalReferrals: 8,
      successfulReferrals: 6,
      totalRewards: 12,
      availableBalance: 350,
      lifetimeEarnings: 850,
      currentLevel: 'gold',
      nextLevelRequirements: 'أحل 15 طالب للوصول للمستوى البلاتيني',
      statistics: {
        thisMonth: {
          referrals: 3,
          rewards: 4,
          earnings: 150,
        },
        thisYear: {
          referrals: 8,
          rewards: 12,
          earnings: 850,
        },
        allTime: {
          referrals: 8,
          rewards: 12,
          earnings: 850,
        },
      },
      achievements: [
        {
          id: '1',
          title: 'أول إحالة',
          description: 'أحلت طالبك الأول بنجاح',
          icon: '🎉',
          unlockedAt: new Date(Date.now() - 7776000000).toISOString(),
          reward: {
            type: 'credit',
            value: 50,
            description: 'رصيد 50 جنيهًا',
          },
        },
        {
          id: '2',
          title: 'محيل نجم',
          description: 'أحلت 5 طلاب بنجاح',
          icon: '⭐',
          unlockedAt: new Date(Date.now() - 2592000000).toISOString(),
          reward: {
            type: 'coupon',
            value: 20,
            couponCode: 'STAR20',
            description: 'كوبون خصم 20%',
          },
        },
      ],
    };

    // البرامج النشطة
    const mockPrograms: ReferralProgram[] = [
      {
        id: '1',
        title: 'برنامج إحالة الطلاب',
        description: 'احصل على مكافآت عند إحالة طلاب جدد للمنصة',
        isActive: true,
        type: 'single_reward',
        rewards: {
          referrerReward: {
            type: 'cash',
            value: 100,
            description: 'مكافأة نقدية 100 جنيه لكل طالب جديد',
          },
          refereeReward: {
            type: 'coupon',
            value: 15,
            couponCode: 'WELCOME15',
            description: 'خصم 15% للطالب الجديد',
          },
        },
        conditions: {
          requiresActivation: true,
          requiresPurchase: true,
          minPurchaseAmount: 200,
          timeLimit: 30,
          applicableRoles: ['student', 'parent'],
          maxRewardsPerUser: 20,
        },
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-12-31T23:59:59Z',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        statistics: {
          totalReferrals: 150,
          successfulReferrals: 95,
          totalRewards: 190,
          totalValue: 19000,
          conversionRate: 63.3,
          topReferrers: [],
        },
      },
      {
        id: '2',
        title: 'برنامج الإحالة المتدرج',
        description: 'مكافآت متزايدة كلما أحلت أكثر',
        isActive: true,
        type: 'milestone_based',
        rewards: {
          referrerReward: {
            type: 'cash',
            value: 50,
            description: 'مكافأة أساسية 50 جنيهًا + مكافآت إضافية',
          },
          refereeReward: {
            type: 'discount',
            value: 20,
            description: 'خصم 20% للطالب الجديد',
          },
          bonusRewards: [
            {
              condition: '5 إحالات ناجحة',
              reward: {
                type: 'cash',
                value: 200,
                description: 'مكافأة إضافية 200 جنيه',
              },
              achievedCount: 1,
            },
            {
              condition: '10 إحالات ناجحة',
              reward: {
                type: 'cash',
                value: 500,
                description: 'مكافأة إضافية 500 جنيه',
              },
              achievedCount: 0,
            },
          ],
        },
        conditions: {
          requiresActivation: true,
          requiresPurchase: true,
          minPurchaseAmount: 150,
          applicableRoles: ['student', 'parent'],
        },
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-12-31T23:59:59Z',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        statistics: {
          totalReferrals: 85,
          successfulReferrals: 62,
          totalRewards: 124,
          totalValue: 8500,
          conversionRate: 72.9,
          topReferrers: [],
        },
      },
    ];

    // إحالات المستخدم
    const mockReferrals: UserReferral[] = [
      {
        id: '1',
        referrerId: user?.id || '1',
        referrerName: user?.name || 'المستخدم',
        refereeId: '2',
        refereeName: 'أحمد محمد',
        refereeEmail: 'ahmed@example.com',
        referralCode: 'STUDENT2024',
        programId: '1',
        status: 'completed',
        createdAt: new Date(Date.now() - 1296000000).toISOString(), // منذ 15 يوم
        activatedAt: new Date(Date.now() - 1209600000).toISOString(), // منذ 14 يوم
        completedAt: new Date(Date.now() - 1123200000).toISOString(), // منذ 13 يوم
        rewardsIssued: [
          {
            id: 'r1',
            recipientId: user?.id || '1',
            recipientType: 'referrer',
            reward: {
              type: 'cash',
              value: 100,
              description: 'مكافأة إحالة طالب جديد',
            },
            issuedAt: new Date(Date.now() - 1123200000).toISOString(),
            claimedAt: new Date(Date.now() - 1036800000).toISOString(),
            status: 'claimed',
          },
        ],
        metadata: {
          source: 'whatsapp',
          campaign: 'student_referral_2024',
        },
      },
      {
        id: '2',
        referrerId: user?.id || '1',
        referrerName: user?.name || 'المستخدم',
        refereeId: '3',
        refereeName: 'فاطمة علي',
        refereeEmail: 'fatima@example.com',
        referralCode: 'STUDENT2024',
        programId: '1',
        status: 'activated',
        createdAt: new Date(Date.now() - 432000000).toISOString(), // منذ 5 أيام
        activatedAt: new Date(Date.now() - 345600000).toISOString(), // منذ 4 أيام
        rewardsIssued: [],
        metadata: {
          source: 'telegram',
          campaign: 'student_referral_2024',
        },
      },
    ];

    setUserProfile(mockProfile);
    setActivePrograms(mockPrograms);
    setUserReferrals(mockReferrals);
    setIsLoading(false);
  }, [user]);

  // الحصول على أيقونة المستوى
  const getLevelIcon = (level: ReferralLevel) => {
    switch (level) {
      case 'bronze':
        return <span className='w-6 h-6 text-orange-600'>🥉</span>;
      case 'silver':
        return <span className='w-6 h-6 text-gray-500'>🥈</span>;
      case 'gold':
        return <span className='w-6 h-6 text-yellow-500'>🏆</span>;
      case 'platinum':
        return <span className='w-6 h-6 text-blue-600'>👑</span>;
      case 'diamond':
        return <span className='w-6 h-6 text-purple-600'>💎</span>;
      default:
        return <span className='w-6 h-6 text-gray-400'>🏅</span>;
    }
  };

  // الحصول على لون المستوى
  const getLevelColor = (level: ReferralLevel) => {
    switch (level) {
      case 'bronze':
        return 'from-orange-500 to-orange-700';
      case 'silver':
        return 'from-gray-400 to-gray-600';
      case 'gold':
        return 'from-yellow-400 to-yellow-600';
      case 'platinum':
        return 'from-blue-500 to-blue-700';
      case 'diamond':
        return 'from-purple-500 to-purple-700';
      default:
        return 'from-gray-300 to-gray-500';
    }
  };

  // نسخ النص
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(''), 2000);
    });
  };

  // مشاركة الرابط
  const shareLink = (platform: string, referralUrl: string) => {
    const message = `انضم إلى منصة Class-Tech التعليمية واحصل على خصم خاص! ${referralUrl}`;

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
        break;
      case 'telegram':
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(
            referralUrl
          )}&text=${encodeURIComponent(message)}`
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(
            'دعوة للانضمام إلى Class-Tech'
          )}&body=${encodeURIComponent(message)}`
        );
        break;
    }
  };

  // تنسيق العملة
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'EGP',
    }).format(amount);
  };

  // تنسيق التاريخ
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-6'>
      <div className='max-w-6xl mx-auto space-y-6'>
        {/* رأس الصفحة */}
        <div className='text-center mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2'>
            نظام الإحالة والمكافآت
          </h1>
          <p className='text-gray-600 dark:text-gray-400'>
            شارك المنصة مع الأصدقاء واحصل على مكافآت رائعة
          </p>
        </div>

        {/* بطاقة الملف الشخصي */}
        <div className='bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-4'>
              <div
                className={`w-16 h-16 rounded-full bg-gradient-to-r ${getLevelColor(
                  userProfile!.currentLevel
                )} flex items-center justify-center`}
              >
                {getLevelIcon(userProfile!.currentLevel)}
              </div>
              <div>
                <h2 className='text-xl font-bold text-gray-900 dark:text-white'>
                  {user?.name} - مستوى{' '}
                  {userProfile!.currentLevel === 'gold'
                    ? 'ذهبي'
                    : userProfile!.currentLevel}
                </h2>
                <p className='text-gray-600 dark:text-gray-400'>
                  الكود الخاص بك: {userProfile!.referralCode}
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                copyToClipboard(userProfile!.referralCode, 'كود الإحالة')
              }
              className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
            >
              <span className='w-4 h-4 text-center'>📋</span>
              {copiedText === 'كود الإحالة' ? 'تم النسخ!' : 'نسخ الكود'}
            </button>
          </div>

          {/* إحصائيات سريعة */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
            <div className='text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg'>
              <span className='w-6 h-6 text-blue-600 mx-auto mb-2'>👥</span>
              <div className='text-2xl font-bold text-blue-600'>
                {userProfile!.totalReferrals}
              </div>
              <div className='text-sm text-gray-600 dark:text-gray-400'>
                إجمالي الإحالات
              </div>
            </div>
            <div className='text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg'>
              <span className='w-6 h-6 text-green-600 mx-auto mb-2'>✅</span>
              <div className='text-2xl font-bold text-green-600'>
                {userProfile!.successfulReferrals}
              </div>
              <div className='text-sm text-gray-600 dark:text-gray-400'>
                إحالات ناجحة
              </div>
            </div>
            <div className='text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg'>
              <span className='w-6 h-6 text-yellow-600 mx-auto mb-2'>🎁</span>
              <div className='text-2xl font-bold text-yellow-600'>
                {userProfile!.totalRewards}
              </div>
              <div className='text-sm text-gray-600 dark:text-gray-400'>
                مكافآت مستلمة
              </div>
            </div>
            <div className='text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg'>
              <span className='w-6 h-6 text-purple-600 mx-auto mb-2'>🪙</span>
              <div className='text-2xl font-bold text-purple-600'>
                {formatCurrency(userProfile!.availableBalance)}
              </div>
              <div className='text-sm text-gray-600 dark:text-gray-400'>
                الرصيد المتاح
              </div>
            </div>
          </div>

          {/* شريط التقدم للمستوى التالي */}
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                التقدم للمستوى التالي
              </span>
              <span className='text-sm text-gray-600 dark:text-gray-400'>
                {userProfile!.successfulReferrals}/15 إحالة
              </span>
            </div>
            <div className='w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2'>
              <div
                className={`bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ${
                  userProfile!.successfulReferrals >= 15
                    ? 'w-full'
                    : userProfile!.successfulReferrals >= 12
                    ? 'w-4/5'
                    : userProfile!.successfulReferrals >= 9
                    ? 'w-3/5'
                    : userProfile!.successfulReferrals >= 6
                    ? 'w-2/5'
                    : userProfile!.successfulReferrals >= 3
                    ? 'w-1/5'
                    : 'w-1/12'
                }`}
              />
            </div>
            <p className='text-xs text-gray-600 dark:text-gray-400 mt-2'>
              {userProfile!.nextLevelRequirements}
            </p>
          </div>
        </div>

        {/* البرامج النشطة */}
        <div className='grid md:grid-cols-2 gap-6'>
          {activePrograms.map(program => (
            <div
              key={program.id}
              className='bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700'
            >
              <div className='flex items-start justify-between mb-4'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
                    {program.title}
                  </h3>
                  <p className='text-gray-600 dark:text-gray-400 text-sm'>
                    {program.description}
                  </p>
                </div>
                <span className='bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs px-2 py-1 rounded-full'>
                  نشط
                </span>
              </div>

              <div className='space-y-3 mb-4'>
                <div className='flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg'>
                  <div className='flex items-center gap-2'>
                    <span className='w-4 h-4 text-blue-600'>🎁</span>
                    <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                      مكافأتك
                    </span>
                  </div>
                  <span className='font-bold text-blue-600'>
                    {program.rewards.referrerReward.type === 'cash'
                      ? formatCurrency(program.rewards.referrerReward.value)
                      : `${program.rewards.referrerReward.value}%`}
                  </span>
                </div>

                <div className='flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg'>
                  <div className='flex items-center gap-2'>
                    <span className='w-4 h-4 text-green-600'>👥</span>
                    <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                      مكافأة الصديق
                    </span>
                  </div>
                  <span className='font-bold text-green-600'>
                    {program.rewards.refereeReward.type === 'coupon' ||
                    program.rewards.refereeReward.type === 'discount'
                      ? `خصم ${program.rewards.refereeReward.value}%`
                      : formatCurrency(program.rewards.refereeReward.value)}
                  </span>
                </div>
              </div>

              {program.rewards.bonusRewards &&
                program.rewards.bonusRewards.length > 0 && (
                  <div className='mb-4'>
                    <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                      مكافآت إضافية:
                    </h4>
                    <div className='space-y-2'>
                      {program.rewards.bonusRewards.map((bonus, index) => (
                        <div
                          key={index}
                          className='flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded'
                        >
                          <span className='text-sm text-gray-600 dark:text-gray-400'>
                            {bonus.condition}
                          </span>
                          <div className='flex items-center gap-2'>
                            <span className='text-sm font-medium text-yellow-600'>
                              {formatCurrency(bonus.reward.value)}
                            </span>
                            {bonus.achievedCount > 0 && (
                              <span className='w-4 h-4 text-green-600'>✅</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <button
                onClick={() => {
                  setSelectedProgram(program);
                  setShowShareModal(true);
                }}
                className='w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all'
              >
                <span className='w-4 h-4'>📤</span>
                شارك الآن
              </button>
            </div>
          ))}
        </div>

        {/* الإنجازات */}
        <div className='bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2'>
            <span className='w-5 h-5 text-yellow-500'>⭐</span>
            إنجازاتك
          </h3>
          <div className='grid md:grid-cols-2 gap-4'>
            {userProfile!.achievements.map(achievement => (
              <div
                key={achievement.id}
                className='flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700'
              >
                <div className='text-3xl'>{achievement.icon}</div>
                <div className='flex-1'>
                  <h4 className='font-medium text-gray-900 dark:text-white'>
                    {achievement.title}
                  </h4>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    {achievement.description}
                  </p>
                  <p className='text-xs text-gray-500 dark:text-gray-500 mt-1'>
                    تم في {formatDate(achievement.unlockedAt)}
                  </p>
                </div>
                {achievement.reward && (
                  <div className='text-right'>
                    <div className='text-sm font-medium text-green-600'>
                      {achievement.reward.type === 'cash'
                        ? formatCurrency(achievement.reward.value)
                        : achievement.reward.description}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* سجل الإحالات */}
        <div className='bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2'>
            <span className='w-5 h-5 text-blue-600'>👥</span>
            سجل الإحالات
          </h3>
          <div className='space-y-4'>
            {userReferrals.map(referral => (
              <div
                key={referral.id}
                className='flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg'
              >
                <div className='flex items-center gap-4'>
                  <div className='w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold'>
                    {referral.refereeName.charAt(0)}
                  </div>
                  <div>
                    <h4 className='font-medium text-gray-900 dark:text-white'>
                      {referral.refereeName}
                    </h4>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      {referral.refereeEmail}
                    </p>
                    <p className='text-xs text-gray-500 dark:text-gray-500'>
                      {formatDate(referral.createdAt)}
                    </p>
                  </div>
                </div>
                <div className='text-left'>
                  <div
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      referral.status === 'completed'
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : referral.status === 'activated'
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                    }`}
                  >
                    {referral.status === 'completed' && 'مكتملة'}
                    {referral.status === 'activated' && 'مفعلة'}
                    {referral.status === 'pending' && 'في الانتظار'}
                  </div>
                  {referral.rewardsIssued.length > 0 && (
                    <p className='text-sm text-green-600 mt-1'>
                      مكافأة:{' '}
                      {formatCurrency(referral.rewardsIssued[0].reward.value)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* مودال المشاركة */}
        {showShareModal && selectedProgram && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
            <div className='bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  مشاركة رابط الإحالة
                </h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                >
                  ✕
                </button>
              </div>

              <div className='space-y-4'>
                {/* الرابط */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    رابط الإحالة الخاص بك
                  </label>
                  <div className='flex items-center gap-2'>
                    <input
                      type='text'
                      value={`https://class-tech.com/ref/${
                        userProfile!.referralCode
                      }`}
                      readOnly
                      aria-label='رابط الإحالة'
                      placeholder='رابط الإحالة'
                      className='flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm'
                    />
                    <button
                      onClick={() =>
                        copyToClipboard(
                          `https://class-tech.com/ref/${
                            userProfile!.referralCode
                          }`,
                          'الرابط'
                        )
                      }
                      aria-label='نسخ رابط الإحالة'
                      className='px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                    >
                      <span className='w-4 h-4 text-center'>📋</span>
                    </button>
                  </div>
                </div>

                {/* أزرار المشاركة */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
                    شارك عبر
                  </label>
                  <div className='grid grid-cols-3 gap-3'>
                    <button
                      onClick={() =>
                        shareLink(
                          'whatsapp',
                          `https://class-tech.com/ref/${
                            userProfile!.referralCode
                          }`
                        )
                      }
                      className='flex flex-col items-center gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors'
                    >
                      <span className='w-6 h-6 text-green-600'>⭐</span>
                      <span className='text-xs text-gray-600 dark:text-gray-400'>
                        واتساب
                      </span>
                    </button>

                    <button
                      onClick={() =>
                        shareLink(
                          'telegram',
                          `https://class-tech.com/ref/${
                            userProfile!.referralCode
                          }`
                        )
                      }
                      className='flex flex-col items-center gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors'
                    >
                      <span className='w-6 h-6 text-blue-600'>⭐</span>
                      <span className='text-xs text-gray-600 dark:text-gray-400'>
                        تيليجرام
                      </span>
                    </button>

                    <button
                      onClick={() =>
                        shareLink(
                          'email',
                          `https://class-tech.com/ref/${
                            userProfile!.referralCode
                          }`
                        )
                      }
                      className='flex flex-col items-center gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                    >
                      <span className='w-6 h-6 text-gray-600'>⭐</span>
                      <span className='text-xs text-gray-600 dark:text-gray-400'>
                        إيميل
                      </span>
                    </button>
                  </div>
                </div>

                {/* رسالة المشاركة المقترحة */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                    رسالة مقترحة
                  </label>
                  <textarea
                    value={`🎓 انضم إلى منصة Class-Tech التعليمية الرائعة!\n\n✨ احصل على خصم ${
                      selectedProgram.rewards.refereeReward.value
                    }% عند التسجيل\n🎯 دروس تفاعلية ومعلمين مختصين\n📚 مناهج شاملة وحديثة\n\nسجل الآن: https://class-tech.com/ref/${
                      userProfile!.referralCode
                    }`}
                    readOnly
                    rows={6}
                    aria-label='رسالة المشاركة المقترحة'
                    placeholder='رسالة المشاركة'
                    className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none'
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralSystem;







